import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Divider, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SeoHead from '../components/common/SeoHead';

const Faq = () => {
  // FAQ页面的结构化数据
  const faqSchemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "如何选择适合自己的水晶?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "选择水晶首先要了解自己的需求和不同水晶的能量特性。例如，紫水晶有助于平静心灵和提升灵性，而粉晶则有助于增强爱的能量和人际关系。您可以根据直觉选择吸引您的水晶，或参考我们的水晶属性指南寻找符合您特定需求的水晶。"
        }
      },
      {
        "@type": "Question",
        "name": "如何预约命理师咨询?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "在我们的网站上，您可以浏览命理师列表，查看每位命理师的专长、评价和可预约时段。选定命理师后，点击预约按钮，选择您方便的日期和时间，完成支付即可成功预约。预约确认后，您将收到确认邮件，里面包含咨询详情和准备事项。"
        }
      },
      {
        "@type": "Question",
        "name": "水晶需要如何保养？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "水晶需要定期净化和充能。常见的净化方法包括月光浴、阳光浴(注意某些水晶如紫晶不适合长时间阳光曝晒)、埋入盐中或放置在净化水晶如白水晶簇上。佩戴时避免接触化学品、香水或洗浴用品，也应避免剧烈碰撞。建议每月至少净化一次，特别是在情绪波动较大时。"
        }
      },
      {
        "@type": "Question",
        "name": "AI算命的准确性如何？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "我们的AI算命服务结合了传统命理学知识和现代人工智能技术，通过分析您的生辰八字和问题，提供有指导意义的解读。虽然AI算命可以提供有价值的参考，但每个人的命运都有其复杂性和可变性。对于更深入、个性化的解读，我们建议预约我们的专业命理师进行一对一咨询。"
        }
      },
      {
        "@type": "Question",
        "name": "购买水晶产品的配送时间是多久？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "我们的水晶产品通常在1-2个工作日内发货。新加坡本地订单一般在发货后1-3个工作日送达，国际订单则根据目的地不同，通常需要7-14个工作日。所有订单发出后会提供追踪号码，您可以随时查询物流状态。如有特殊送货要求，请在下单时备注或联系我们的客服。"
        }
      }
    ]
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* SEO优化 */}
      <SeoHead
        title="常见问题解答 | 水晶选择与保养 | 命理咨询 | DestinyCrystal"
        description="了解有关水晶选择、保养、命理师预约和AI算命的常见问题解答。DestinyCrystal为您提供全面的指南，帮助您更好地使用我们的产品和服务。"
        canonicalUrl="https://destiny-crystal.com/faq"
        keywords={["常见问题", "水晶选择", "水晶保养", "命理师预约", "AI算命", "DestinyCrystal常见问题"]}
        schemaData={faqSchemaData}
      />
      
      <Typography variant="h4" align="center" gutterBottom>
        常见问题解答 (FAQ)
      </Typography>
      <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 4 }}>
        DestinyCrystal为您解答关于水晶、命理和服务的常见疑问
      </Typography>
      
      <Divider sx={{ mb: 4 }} />
      
      <Paper elevation={2} sx={{ p: 2, mb: 6 }}>
        <Typography variant="h5" gutterBottom color="primary.main">
          关于水晶
        </Typography>
        
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography fontWeight="medium">如何选择适合自己的水晶?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              选择水晶首先要了解自己的需求和不同水晶的能量特性。例如，紫水晶有助于平静心灵和提升灵性，而粉晶则有助于增强爱的能量和人际关系。您可以根据直觉选择吸引您的水晶，或参考我们的水晶属性指南寻找符合您特定需求的水晶。
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2a-content"
            id="panel2a-header"
          >
            <Typography fontWeight="medium">水晶需要如何保养？</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              水晶需要定期净化和充能。常见的净化方法包括月光浴、阳光浴(注意某些水晶如紫晶不适合长时间阳光曝晒)、埋入盐中或放置在净化水晶如白水晶簇上。佩戴时避免接触化学品、香水或洗浴用品，也应避免剧烈碰撞。建议每月至少净化一次，特别是在情绪波动较大时。
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel3a-content"
            id="panel3a-header"
          >
            <Typography fontWeight="medium">水晶的能量会消失吗？</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              水晶的能量不会消失，但会随着使用而变得不那么活跃。就像电池需要充电一样，水晶也需要定期净化和充能。长期使用后，水晶会吸收环境和佩戴者的能量，这可能会影响其原有的能量特性。通过适当的保养方法，可以保持水晶能量的活跃状态。
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Paper>
      
      <Paper elevation={2} sx={{ p: 2, mb: 6 }}>
        <Typography variant="h5" gutterBottom color="primary.main">
          关于命理服务
        </Typography>
        
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel4a-content"
            id="panel4a-header"
          >
            <Typography fontWeight="medium">如何预约命理师咨询?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              在我们的网站上，您可以浏览命理师列表，查看每位命理师的专长、评价和可预约时段。选定命理师后，点击预约按钮，选择您方便的日期和时间，完成支付即可成功预约。预约确认后，您将收到确认邮件，里面包含咨询详情和准备事项。
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel5a-content"
            id="panel5a-header"
          >
            <Typography fontWeight="medium">AI算命的准确性如何？</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              我们的AI算命服务结合了传统命理学知识和现代人工智能技术，通过分析您的生辰八字和问题，提供有指导意义的解读。虽然AI算命可以提供有价值的参考，但每个人的命运都有其复杂性和可变性。对于更深入、个性化的解读，我们建议预约我们的专业命理师进行一对一咨询。
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel6a-content"
            id="panel6a-header"
          >
            <Typography fontWeight="medium">需要准备哪些信息来获得更准确的命理解读？</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              准确的出生信息是最重要的，包括出生年、月、日、时（农历或阳历，建议提供准确的出生时辰）。此外，明确您想咨询的问题领域（如事业、爱情、财运等）也有助于获得更有针对性的解读。对于线上咨询，确保您的网络连接良好，并准备好可能需要的个人照片或其他命理师要求的资料。
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Paper>
      
      <Paper elevation={2} sx={{ p: 2, mb: 6 }}>
        <Typography variant="h5" gutterBottom color="primary.main">
          关于购物与配送
        </Typography>
        
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel7a-content"
            id="panel7a-header"
          >
            <Typography fontWeight="medium">购买水晶产品的配送时间是多久？</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              我们的水晶产品通常在1-2个工作日内发货。新加坡本地订单一般在发货后1-3个工作日送达，国际订单则根据目的地不同，通常需要7-14个工作日。所有订单发出后会提供追踪号码，您可以随时查询物流状态。如有特殊送货要求，请在下单时备注或联系我们的客服。
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel8a-content"
            id="panel8a-header"
          >
            <Typography fontWeight="medium">如何退换产品？</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              我们提供7天无理由退换服务（需保持产品原包装完好未使用）。如产品有质量问题，可在收到产品14天内申请退换。退换流程：在个人中心-订单历史中找到相应订单，点击"申请退换"，填写原因并上传产品照片，客服审核后会与您联系退换事宜。运费规则：质量问题退换由我们承担运费，非质量问题退换需客户承担退货运费。
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel9a-content"
            id="panel9a-header"
          >
            <Typography fontWeight="medium">支持哪些支付方式？</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              我们支持多种支付方式，包括主流信用卡（Visa、MasterCard、American Express）、PayPal国际支付、银行转账等。对于新加坡本地客户，还支持PayNow快捷支付。所有支付渠道均采用加密技术，确保您的支付信息安全。如遇支付问题，请联系我们的客服获取帮助。
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Paper>
      
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 8 }}>
        如果您有其他问题，欢迎<a href="/contact" style={{ color: '#9c27b0', textDecoration: 'none' }}>联系我们</a>，我们会尽快回复您。
      </Typography>
    </Container>
  );
};

export default Faq;